import {
  StoreScheduleCreateRequest,
  StoreScheduleResponse,
  StoreScheduleUpdateRequest,
} from '@ydistri/api-sdk';
import { apiSlice, ErrorLike, errorTransform } from '../../../apis/api';
import { getTags } from '../../../apis/apiLib';
import { signalrClient } from '../../../signalr/client';
import { StoreSchedulesCollection } from '../../../swagger/collections';
import { SignalProjectConfigurationChanged } from '../common/administrationItemsTypes';

const { TAGS, TAGS_ARRAY } = getTags('storesScheduleAdministration');
// The configuration screen's derived list changes with every register write. SignalR carries that
// to other sessions; invalidating here is what makes it immediate for the one doing the writing,
// and what still works when the connection is down.
const { TAGS: EXCLUSION_TAGS, TAGS_ARRAY: EXCLUSION_TAGS_ARRAY } =
  getTags('storeScheduleExclusions');

export const apiStoreSchedules = apiSlice
  .enhanceEndpoints({ addTagTypes: [...TAGS_ARRAY, ...EXCLUSION_TAGS_ARRAY] })
  .injectEndpoints({
    endpoints: builder => ({
      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- the collection takes no arguments
      getStoreSchedules: builder.query<StoreScheduleResponse[], void>({
        queryFn: async () => {
          try {
            const response = await StoreSchedulesCollection.getStoreSchedules();
            return { data: response.data.data };
          } catch (error) {
            // A plain copy: the raw axios error is not serializable and would not be stored whole.
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- generated SDK error
            return { error: errorTransform(error as ErrorLike) };
          }
        },
        providesTags: [TAGS.storeSchedulesList],
        async onCacheEntryAdded(arg, api) {
          try {
            await api.cacheDataLoaded;

            // The register is project-wide, so another session's write belongs in this list too.
            const listener = (signal: SignalProjectConfigurationChanged) => {
              if (signal.entityType !== 'StoreSchedule') return;
              api.dispatch(apiStoreSchedules.util.invalidateTags([TAGS.storeSchedulesList]));
            };

            signalrClient.on('ProjectConfigurationChanged', listener);

            api.cacheEntryRemoved.then(() => {
              signalrClient.off('ProjectConfigurationChanged', listener);
            });
          } catch {
            // no-op in case cache entry was removed before data was loaded
          }
          await api.cacheEntryRemoved;
        },
      }),
      createStoreSchedule: builder.mutation<StoreScheduleResponse, StoreScheduleCreateRequest>({
        queryFn: async payload => {
          try {
            const response = await StoreSchedulesCollection.createStoreSchedule(payload);
            return { data: response.data.data };
          } catch (error) {
            // A plain copy: the raw axios error is not serializable and would not be stored whole.
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- generated SDK error
            return { error: errorTransform(error as ErrorLike) };
          }
        },
        invalidatesTags: [TAGS.storeSchedulesList, EXCLUSION_TAGS.storeScheduleExclusions],
      }),
      updateStoreSchedule: builder.mutation<
        StoreScheduleResponse,
        { storeScheduleId: number; payload: StoreScheduleUpdateRequest }
      >({
        queryFn: async ({ storeScheduleId, payload }) => {
          try {
            const response = await StoreSchedulesCollection.putStoreSchedule(
              storeScheduleId,
              payload,
            );
            return { data: response.data.data };
          } catch (error) {
            // A plain copy: the raw axios error is not serializable and would not be stored whole.
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- generated SDK error
            return { error: errorTransform(error as ErrorLike) };
          }
        },
        invalidatesTags: (result, error, arg) => [
          TAGS.storeSchedulesList,
          { type: TAGS.storeSchedule, id: arg.storeScheduleId },
          EXCLUSION_TAGS.storeScheduleExclusions,
        ],
      }),
      deleteStoreSchedule: builder.mutation<void, number>({
        queryFn: async storeScheduleId => {
          try {
            await StoreSchedulesCollection.deleteStoreSchedule(storeScheduleId);
            return { data: undefined };
          } catch (error) {
            // A plain copy: the raw axios error is not serializable and would not be stored whole.
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- generated SDK error
            return { error: errorTransform(error as ErrorLike) };
          }
        },
        invalidatesTags: (result, error, storeScheduleId) => [
          TAGS.storeSchedulesList,
          { type: TAGS.storeSchedule, id: storeScheduleId },
          EXCLUSION_TAGS.storeScheduleExclusions,
        ],
      }),
    }),
  });

export const {
  useGetStoreSchedulesQuery,
  useCreateStoreScheduleMutation,
  useUpdateStoreScheduleMutation,
  useDeleteStoreScheduleMutation,
} = apiStoreSchedules;
